import { escapeHtml } from "@medplum/core";

/**
 * True when a string has no meaningful text (after stripping tags).
 * TipTap / RichTextField stores empty content as HTML like `<p></p>`, not `""`.
 */
export function isHtmlEmpty(html: string | undefined | null): boolean {
  if (html == null || html === "") return true;
  const text = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .trim();
  return text.length === 0;
}

export function normalizeEmptyHtml(html: string | undefined): string {
  const value = html ?? "";
  return isHtmlEmpty(value) ? "" : value;
}

export function plainTextToHtml(text: string): string {
  if (text === "") return "";
  return `<p>${escapeHtml(text).replace(/\r?\n/g, "<br>")}</p>`;
}

/** Spaces one tab stands in for; a conventional tab width, not tied to any editor setting. */
const TAB_WIDTH = 4;

/** The block tags TipTap opens a document with, as `RichTextField` serializes it. */
const LEADING_BLOCK_TAG = /^\s*<(?:p|h[1-6]|ul|ol|blockquote)(?:\s[^>]*)?>/i;

/**
 * True when a value is already rich text markup, judged by the block tag it opens with.
 *
 * Deliberately narrow: consent prose legitimately contains characters that look like markup
 * ("a < b", "I agree <3"), and treating those as HTML would leave them unconverted forever.
 *
 * @param value - the stored consent Summary or Body.
 * @returns true when the value opens with a block tag and needs no conversion.
 */
export function startsWithBlockTag(value: string | undefined | null): boolean {
  if (value == null || value === "") return false;
  return LEADING_BLOCK_TAG.test(value);
}

/**
 * Converts plain text into the HTML shape TipTap produces for the same document, so content
 * authored before rich text formatting keeps its paragraphs, line breaks and indentation.
 *
 * Richer than {@link plainTextToHtml}, which wraps the whole value in one paragraph: this one
 * splits paragraphs on blank lines and keeps indentation. Both are in use; see PE-5094.
 *
 * A blank line starts a new paragraph, a single newline becomes a line break, and indentation —
 * tabs, or runs of two or more spaces — becomes non-breaking spaces so it survives. Only tags in
 * `react-pdf-html`'s table reach the output; anything else would be dropped from the exported PDF
 * along with its contents.
 *
 * @param value - plain text, which may contain characters that look like markup.
 * @returns HTML paragraphs, or an empty string when the value holds no text.
 */
export function plainTextToRichTextHtml(value: string | undefined | null): string {
  if (value == null || value.trim() === "") return "";

  const escaped = value
    .replace(/\r\n?/g, "\n")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return escaped
    .split(/\n[ \t]*\n+/)
    .reduce<string[]>((paragraphs, block) => {
      const paragraph = buildParagraph(block);
      if (paragraph !== "") paragraphs.push(`<p>${paragraph}</p>`);
      return paragraphs;
    }, [])
    .join("");
}

/**
 * One paragraph's inner HTML, with each source line separated by a line break.
 *
 * Whitespace at the end of a line and blank lines at either end of the block are dropped first:
 * both are invisible in the source text and in HTML, and keeping them would turn into a visible
 * `&nbsp;` run or a leading `<br>` once the rest of this function makes whitespace significant.
 *
 * @param block - One run of source lines, already escaped, with no blank line inside it.
 * @returns The `<br>`-joined lines, or an empty string when the block holds no text.
 */
function buildParagraph(block: string): string {
  const lines = block.split("\n").map((line) => line.replace(/[ \t]+$/, ""));

  while (lines.length > 0 && lines[0] === "") lines.shift();
  while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();

  return lines.map(preserveIndentation).join("<br>");
}

/**
 * Keeps indentation visible in the form builder, client view, and card: a tab counts as
 * {@link TAB_WIDTH} spaces, and a run of two or more spaces becomes the same number of
 * non-breaking spaces. A single space is ordinary word spacing.
 *
 * A literal tab collapses to one space in both HTML and `react-pdf-html`. Expanding tabs to spaces
 * restores the indentation on web surfaces, but the PDF still flattens runs of `&nbsp;` to a single
 * space per D14 — a `react-pdf-html` library limitation.
 *
 * @param line - one source line with trailing whitespace removed; tabs and leading spaces
 *               remain for indentation processing.
 * @returns the line with tabs expanded to spaces and runs of 2+ spaces converted to `&nbsp;`.
 */
function preserveIndentation(line: string): string {
  return line
    .replace(/\t/g, " ".repeat(TAB_WIDTH))
    .replace(/ {2,}/g, (spaces) => "&nbsp;".repeat(spaces.length));
}
